import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { RequireAuth } from "@/auth/RequireAuth";
import { useAuth } from "@/auth/AuthProvider";
import { isMarketingOnly } from "@/lib/roles";
import { CommandCenterShell } from "@/components/CommandCenterShell";
import { Shell } from "@/components/Shell";
import { LoginPage } from "@/pages/LoginPage";
import { SetPasswordPage } from "@/pages/SetPasswordPage";
import { ResetPasswordConfirmPage } from "@/pages/ResetPasswordConfirmPage";
import { SalesDashboardPage } from "@/pages/SalesDashboardPage";
import { SalesPipelinePage } from "@/pages/SalesPipelinePage";
import { WebsiteOrdersPage } from "@/pages/WebsiteOrdersPage";
import { CompanyStoresListPage } from "@/pages/CompanyStoresListPage";
import { CompanyStoreEditorPage } from "@/pages/CompanyStoreEditorPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { ManagementDashboardPage } from "@/pages/ManagementDashboardPage";
import { FinanceDashboardPage } from "@/pages/FinanceDashboardPage";
import { InvoicingPage } from "@/pages/InvoicingPage";
import { CommissionsPayoutPage } from "@/pages/CommissionsPayoutPage";
import { QuickBooksExportPage } from "@/pages/QuickBooksExportPage";
import { CommissionSettingsPage } from "@/pages/CommissionSettingsPage";
import { CommissionStatementPage } from "@/pages/commissions/CommissionStatementPage";
import { InvoiceDetailPage } from "@/pages/InvoiceDetailPage";
import { BillingPage } from "@/pages/BillingPage";
import { VendorCafePage } from "@/pages/VendorCafePage";
import { BillDetailPage } from "@/pages/BillDetailPage";
import { ReportsListPage } from "@/pages/ReportsListPage";
import { CsImportSearchPage } from "@/pages/cs-import/CsImportSearchPage";
import { CsImportProjectPage } from "@/pages/cs-import/CsImportProjectPage";
import { CsImportClientPage } from "@/pages/cs-import/CsImportClientPage";
import { CsImportLogPage } from "@/pages/cs-import/CsImportLogPage";
import { ReportSalesDashboardPage } from "@/pages/reports/ReportSalesDashboardPage";
import { ReportSalesPipelinePage } from "@/pages/reports/ReportSalesPipelinePage";
import { ReportSalesActivityPage } from "@/pages/reports/ReportSalesActivityPage";
import { ReportSalesRepPage } from "@/pages/reports/ReportSalesRepPage";
import { ReportCommissionsPage } from "@/pages/reports/ReportCommissionsPage";
import { ReportSalesComparisonPage } from "@/pages/reports/ReportSalesComparisonPage";
import { ReportAdvertisingPage } from "@/pages/reports/ReportAdvertisingPage";
import { ReportSalesHistoryPage } from "@/pages/reports/ReportSalesHistoryPage";
import { AccessAdminPage } from "@/pages/AccessAdminPage";
import { CatalogProductDetailPage } from "@/pages/CatalogProductDetailPage";
import { CatalogProductsListPage } from "@/pages/CatalogProductsListPage";
import VendorConsolePage from "@/pages/VendorConsolePage";
import VendorsPage from "@/pages/VendorsPage";
import VendorEditPage from "@/pages/VendorEditPage";
import VendorDetailPage from "@/pages/VendorDetailPage";
import { CategoriesAdminPage } from "@/pages/CategoriesAdminPage";
import { ColorsAdminPage } from "@/pages/ColorsAdminPage";
import { ClientsListPage } from "@/pages/ClientsListPage";
import { ClientDetailLayout } from "@/pages/ClientDetailPage";
import { ClientOverviewTab } from "@/pages/ClientOverviewTab";
import { ClientActivityTab } from "@/pages/ClientActivityTab";
import { ClientContactsTab } from "@/pages/ClientContactsTab";
import { ClientAddressesTab } from "@/pages/ClientAddressesTab";
import { ClientProjectsTab } from "@/pages/ClientProjectsTab";
import { ClientSubAccountsTab } from "@/pages/ClientSubAccountsTab";
import { ClientLibraryTab } from "@/pages/ClientLibraryTab";
import { ClientStoreTab } from "@/pages/ClientStoreTab";
import { CommandCenterDashboardPage } from "@/pages/CommandCenterDashboardPage";
import { ComingSoonPage } from "@/pages/ComingSoonPage";
import { OrderDetailPage } from "@/pages/OrderDetailPage";
import { InvoiceRedirect } from "@/pages/InvoiceRedirect";
import { OrderRedirect } from "@/pages/OrderRedirect";
import { PoRedirect } from "@/pages/PoRedirect";
import { PresentationDetailPage } from "@/pages/PresentationDetailPage";
import { PresentationRedirect } from "@/pages/PresentationRedirect";
import { PresentationsListPage } from "@/pages/PresentationsListPage";
import { ProductionTilesPage } from "@/pages/ProductionTilesPage";
import { ProductionReportPage } from "@/pages/ProductionReportPage";
import { PurchaseOrderDetailPage } from "@/pages/PurchaseOrderDetailPage";
import { ProjectDetailLayout } from "@/pages/ProjectDetailPage";
import { ProjectBillsTab } from "@/pages/ProjectBillsTab";
import { ProjectCommissionsTab } from "@/pages/ProjectCommissionsTab";
import { ProjectFilesTab } from "@/pages/ProjectFilesTab";
import { ProjectInvoiceTab } from "@/pages/ProjectInvoiceTab";
import { ProjectOverviewTab } from "@/pages/ProjectOverviewTab";
import { ProjectPosTab } from "@/pages/ProjectPosTab";
import { ProjectPresentationTab } from "@/pages/ProjectPresentationTab";
import { ProjectQuotesTab } from "@/pages/ProjectQuotesTab";
import { QuoteDetailRedirect, QuotesTabRedirect } from "@/pages/QuoteRedirects";
import { ProjectFeedbackTab } from "@/pages/ProjectFeedbackTab";
import { ProjectShippingTab } from "@/pages/ProjectShippingTab";
import { MessagesInboxPage } from "@/pages/MessagesInboxPage";
import { ProjectRequestsPage } from "@/pages/ProjectRequestsPage";
import { ProjectsListPage } from "@/pages/ProjectsListPage";
import { PublicPresentationPage } from "@/pages/PublicPresentationPage";
import { PublicVirtualsPage } from "@/pages/PublicVirtualsPage";
import { QuickVirtualPage } from "@/pages/QuickVirtualPage";
import { PublicOrderPage } from "@/pages/PublicOrderPage";
import { PublicPOPage } from "@/pages/PublicPOPage";
import { PublicInvoicePage } from "@/pages/PublicInvoicePage";
import { PublicProofPage } from "@/pages/PublicProofPage";
import { ProofSendPage } from "@/pages/ProofSendPage";
import { ProofTemplatesPage } from "@/pages/ProofTemplatesPage";
import { PoTemplatesPage } from "@/pages/PoTemplatesPage";

/** Old Work queue / Database links land on the Production Report, filters kept. */
function ToProductionReport() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/production/report", search }} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Self-serve auth — public, no RequireAuth, no app shell (2026-07-20).
          Reached by clicking the invite / password-reset email links. The
          two paths are also in the API's WEB_SPA_PAGE_EXACT allowlist so a
          browser navigation gets the SPA shell instead of raw API JSON. */}
      <Route path="/auth/set-password" element={<SetPasswordPage />} />
      <Route
        path="/auth/password-reset/confirm"
        element={<ResetPasswordConfirmPage />}
      />

      {/* Public presentation share link — no RequireAuth, no app shell.
          Gated only by the unguessable share UUID. Drafts return 404
          even with a valid UUID (server-side check). */}
      <Route path="/p/:uuid" element={<PublicPresentationPage />} />
      <Route path="/v/:token" element={<PublicVirtualsPage />} />

      {/* Public order/estimate share link (PR #154a, 2026-05-26). Same
          UUID-gated, no-RequireAuth pattern as /p/:uuid. Draft orders
          return 404 to anonymous traffic; staff JWT bypasses for
          preview. */}
      <Route path="/o/:uuid" element={<PublicOrderPage />} />

      {/* Public vendor PO view + acknowledge (fixes Workflow Readiness
          BLOCKER B2 — the PO email linked to a dead /po/:uuid route). Same
          UUID-gated, no-RequireAuth pattern as /o/:uuid. Draft POs return
          404 to anonymous vendors; staff JWT bypasses for preview. */}
      <Route path="/po/:uuid" element={<PublicPOPage />} />

      {/* Public invoice share link (2026-06-19). Same UUID-gated,
          no-RequireAuth pattern. The client opens this from the emailed
          link to view their invoice (display-only; manual payment). */}
      <Route path="/i/:uuid" element={<PublicInvoicePage />} />

      {/* Public client proof review — Approve / Request changes (Stephanie
          2026-10-12). Same UUID-gated, no-RequireAuth pattern; never shows
          the PO #, supplier or costs. */}
      <Route path="/proof/:uuid" element={<PublicProofPage />} />

      {/* Command Center — separate top-level shell (dark theme, top nav,
          no sidebar). Mirrors the Z Brand Command Center reference.
          Admin surfaces (Catalog / Categories / Colors / Access) are
          reachable via the Command Center's Backend grid, not the
          Workflow sidebar. */}
      <Route
        element={
          <RequireAuth>
            <CommandCenterShell />
          </RequireAuth>
        }
      >
        <Route path="/command" element={<Navigate to="/command/dashboard" replace />} />
        <Route path="/command/dashboard" element={<CommandCenterDashboardPage />} />
      </Route>

      {/* Workflow — sidebar shell. */}
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<HomeRedirect />} />

        {/* Sales group */}
        <Route path="/sales/dashboard" element={<SalesDashboardPage />} />
        <Route path="/sales/pipeline" element={<SalesPipelinePage />} />
        <Route path="/sales/website-orders" element={<WebsiteOrdersPage />} />
        {/* Company stores (S2, CEO 2026-10-12) — replaced the "Shops · soon" stub. */}
        <Route path="/sales/company-stores" element={<CompanyStoresListPage />} />
        <Route path="/sales/company-stores/new" element={<CompanyStoreEditorPage />} />
        <Route path="/sales/company-stores/:id" element={<CompanyStoreEditorPage />} />
        <Route path="/projects" element={<ProjectsListPage />} />
        {/* Project container — commonsku-style sub-nav with 10 tabs (Tracking
            folded into Overview 2026-07-29; `shipping` route kept as a redirect). */}
        <Route path="/projects/:id" element={<ProjectDetailLayout />}>
          <Route index element={<ProjectOverviewTab />} />
          <Route path="overview" element={<ProjectOverviewTab />} />
          <Route path="presentation" element={<ProjectPresentationTab />} />
          {/* Quotes (CEO 2026-10-15): Estimate + Sales Order are one Quote.
              The old tab + detail paths redirect here so every existing link
              (emails, bookmarks, deep links) lands on the same page. */}
          <Route path="quotes" element={<ProjectQuotesTab />} />
          <Route path="quotes/:orderId" element={<OrderDetailPage />} />
          <Route path="sales-order" element={<Navigate to="../quotes" replace />} />
          {/* Estimate + Quote DETAIL render inside the project
              sub-nav (2026-05-29) so the stage stays highlighted and the
              project context is never lost. */}
          <Route path="sales-order/:orderId" element={<QuoteDetailRedirect />} />
          <Route path="files" element={<ProjectFilesTab />} />
          <Route path="estimate" element={<QuotesTabRedirect />} />
          <Route path="estimate/:orderId" element={<QuoteDetailRedirect />} />
          {/* Presentation DETAIL renders inside the project sub-nav
              (2026-06-01 nav follow-up) so the "Presentation" tab stays
              highlighted and the project context is never lost — same
              pattern as the Estimate / Quote detail above. */}
          <Route
            path="presentation/:presentationId"
            element={<PresentationDetailPage />}
          />
          {/* Tracking tab removed 2026-07-29 (folded into Overview). The
              `shipping` segment survives as a redirect to Overview so legacy
              deep links + browser history never 404. */}
          <Route path="shipping" element={<ProjectShippingTab />} />
          <Route path="pos" element={<ProjectPosTab />} />
          {/* PO + invoice DETAIL render inside the project sub-nav
              (2026-07-25) so the workspace chrome (sub-nav tabs + stage
              rail) survives when a rep follows a NextActionCta deep link to
              send a PO or add tracking — same in-context pattern as the
              Estimate / Quote detail above (2026-05-29). Nested under
              their tab segments so the POs / Invoice tab stays highlighted.
              NOTE: the invoice tab segment is `invoice` (SINGULAR), so the
              detail route is `invoice/:invoiceId`, not `invoices/`. */}
          <Route path="pos/:poId" element={<PurchaseOrderDetailPage />} />
          <Route path="invoice" element={<ProjectInvoiceTab />} />
          <Route path="invoice/:invoiceId" element={<InvoiceDetailPage />} />
          <Route path="bills" element={<ProjectBillsTab />} />
          <Route path="commissions" element={<ProjectCommissionsTab />} />
          <Route path="feedback" element={<ProjectFeedbackTab />} />
        </Route>
        <Route path="/clients" element={<ClientsListPage />} />
        {/* Client container — commonsku-style left sub-nav with 6 tabs
            (2026-05-29 Nav Part 2). Mirrors the /projects/:id pattern:
            bare /clients/:id renders Overview at the index route. */}
        <Route path="/clients/:id" element={<ClientDetailLayout />}>
          <Route index element={<ClientOverviewTab />} />
          <Route path="overview" element={<ClientOverviewTab />} />
          <Route path="activity" element={<ClientActivityTab />} />
          <Route path="contacts" element={<ClientContactsTab />} />
          <Route path="addresses" element={<ClientAddressesTab />} />
          <Route path="projects" element={<ClientProjectsTab />} />
          <Route path="subaccounts" element={<ClientSubAccountsTab />} />
          <Route path="store" element={<ClientStoreTab />} />
          <Route path="library" element={<ClientLibraryTab />} />
        </Route>
        <Route path="/shops" element={<Navigate to="/sales/company-stores" replace />} />
        <Route path="/project-requests" element={<ProjectRequestsPage />} />
        <Route path="/virtuals" element={<QuickVirtualPage />} />
        <Route path="/messages" element={<MessagesInboxPage />} />
        <Route path="/messages/:id" element={<MessagesInboxPage />} />
        <Route path="/presentations" element={<PresentationsListPage />} />
        {/* Standalone /presentations/:id redirects into the project
            sub-nav so global-search / clone / bookmark links land in
            context (mirrors OrderRedirect, Nav Part 1). */}
        <Route path="/presentations/:id" element={<PresentationRedirect />} />

        {/* Production group */}
        {/* Production Report replaces the Work queue (/production) and the
            Database (/production/database); the Calendar is retired. Old
            links and bookmarks redirect, keeping any filters (Stephanie
            2026-10-12). */}
        <Route path="/production" element={<ToProductionReport />} />
        <Route path="/production/dashboard" element={<ProductionTilesPage />} />
        <Route path="/production/report" element={<ProductionReportPage />} />
        <Route path="/production/database" element={<ToProductionReport />} />
        <Route path="/production/calendar" element={<Navigate to="/production/dashboard" replace />} />
        {/* Legacy top-level /pos/:id (emails / bookmarks / global search)
            redirects into the project sub-nav; falls back to standalone for
            production-only POs with no project (2026-07-25). */}
        <Route path="/pos/:id" element={<PoRedirect />} />
        {/* Project-only access (2026-05-29): orders live inside their
            project sub-nav. The global list is removed; legacy / global-
            search / bookmark links to /orders/:id redirect into context. */}
        <Route path="/orders" element={<Navigate to="/projects" replace />} />
        <Route path="/orders/:id" element={<OrderRedirect />} />

        {/* Finance group */}
        <Route path="/finance/dashboard" element={<FinanceDashboardPage />} />
        <Route path="/finance/billing" element={<BillingPage />} />
        <Route path="/bills/:id" element={<BillDetailPage />} />
        <Route path="/finance/invoicing" element={<InvoicingPage />} />
        <Route path="/finance/vendor-cafe" element={<VendorCafePage />} />
        <Route path="/finance/quickbooks" element={<QuickBooksExportPage />} />
        {/* Legacy top-level /invoices/:id (emails / bookmarks / global
            search / InvoicingPage) redirects into the project sub-nav; falls
            back to standalone if the invoice has no project (2026-07-25). */}
        <Route path="/invoices/:id" element={<InvoiceRedirect />} />
        <Route path="/finance/commissions" element={<CommissionsPayoutPage />} />
        <Route
          path="/finance/commissions/settings"
          element={<CommissionSettingsPage />}
        />
        {/* Rep commission statement — every staff user (own only; Finance any rep). */}
        <Route
          path="/finance/commissions/statement"
          element={<CommissionStatementPage />}
        />

        {/* Management group */}
        <Route
          path="/management/dashboard"
          element={<ManagementDashboardPage />}
        />
        <Route
          path="/management/analytics"
          element={<ComingSoonPage title="Management analytics" eta="next sprint" />}
        />

        {/* CS Import — read-only commonsku archive */}
        <Route path="/cs-import" element={<CsImportSearchPage />} />
        <Route path="/cs-import/projects/:id" element={<CsImportProjectPage />} />
        <Route path="/cs-import/clients/:id" element={<CsImportClientPage />} />
        <Route path="/cs-import/log" element={<CsImportLogPage />} />

        {/* Reports */}
        <Route path="/reports" element={<ReportsListPage />} />
        <Route
          path="/reports/sales-dashboard"
          element={<ReportSalesDashboardPage />}
        />
        <Route
          path="/reports/sales-pipeline"
          element={<ReportSalesPipelinePage />}
        />
        <Route
          path="/reports/sales-activity"
          element={<ReportSalesActivityPage />}
        />
        <Route path="/reports/sales-rep" element={<ReportSalesRepPage />} />
        <Route
          path="/reports/commissions"
          element={<ReportCommissionsPage />}
        />
        <Route
          path="/reports/sales-comparison"
          element={<ReportSalesComparisonPage />}
        />
        <Route path="/reports/advertising" element={<ReportAdvertisingPage />} />
        <Route path="/reports/sales-history" element={<ReportSalesHistoryPage />} />

        {/* Admin surfaces — reachable via Command Center Backend grid.
            Routes kept for direct nav / bookmarks; removed from the
            Workflow sidebar so day-to-day reps see a focused IA. */}
        <Route path="/production/vendors" element={<VendorsPage />} />
        <Route path="/production/vendors/:vendorId" element={<VendorEditPage />} />
        <Route path="/admin/vendors" element={<VendorConsolePage />} />
        <Route path="/admin/vendors/:vendorId" element={<VendorDetailPage />} />
        <Route
          path="/admin/catalog/products"
          element={<CatalogProductsListPage />}
        />
        <Route
          path="/admin/catalog/products/:id"
          element={<CatalogProductDetailPage />}
        />
        <Route path="/admin/categories" element={<CategoriesAdminPage />} />
        <Route path="/admin/colors" element={<ColorsAdminPage />} />
        <Route path="/admin/access" element={<AccessAdminPage />} />

        {/* Settings reachable from the user-menu dropdown. */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/proof-templates" element={<ProofTemplatesPage />} />
        <Route path="/settings/po-templates" element={<PoTemplatesPage />} />
        {/* Rep sends a proof to the client (Stephanie 2026-10-12). */}
        <Route path="/proofs/:id/send" element={<ProofSendPage />} />

        {/* Backwards-compat aliases so bookmarks / direct links still work. */}
        <Route path="/dashboard" element={<Navigate to="/sales/dashboard" replace />} />
      </Route>
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

/** Home: the sales dashboard; the Marketing role lands on Reports (CEO 2026-10-21). */
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={isMarketingOnly(user?.roles) ? "/reports" : "/sales/dashboard"} replace />;
}
